import { Controller } from "@hotwired/stimulus"

// AT-1108: filteren van de agenda (epic AT-1100).
//
// Alle kaarten staan al in de DOM, net als in de oude jQuery-versie; het filteren is
// puur tonen en verbergen. Nieuw ten opzichte van de oude twee selects: zoeken, een
// kalender waarin losse dagen te kiezen zijn, en genres die stapelen in plaats van
// elkaar te overschrijven.
//
// De kalenderroosters worden serverside gerenderd (Red2Helper#red2_calendar_months),
// zodat maandnamen en weekindeling in de juiste taal staan en er geen datumbibliotheek
// in de bundel hoeft. Deze controller bladert er alleen doorheen.
export default class extends Controller {
  static targets = [
    "card", "section", "search", "genre", "count", "noResults", "clear",
    "calendarGrid", "calendarLabel", "viewButton",
    "panel", "body", "toggle", "toggleCount",
    "layout", "sidebarToggle", "sidebarCount"
  ]

  static VIEW_KEY = "avayo.red2.agenda.view"
  static SIDEBAR_KEY = "avayo.red2.agenda.filters"

  connect() {
    this.monthIndex = 0
    this.selectedDays = new Set()
    this.#renderCalendar()
    this.#restoreView()
    this.#restoreSidebar()
    this.apply()
  }

  // --- zijbalk wegklappen op desktop --------------------------------------------
  //
  // Op een breed scherm staat de balk naast de kaarten en is er niets te verbergen --
  // behalve de ruimte die hij inneemt. 18rem is bij een klant met een smalle
  // iframe-kolom een kwart van de breedte. Vandaar een knop naast de weergaveschakelaar
  // die hem wegklapt; de keuze blijft staan, net als lijst of raster.
  //
  // Dit is niet hetzelfde als toggleFilters hierboven: dat is de mobiele variant, waar
  // de balk bóven de kaarten staat en dus de agenda onder de vouw duwt.

  toggleSidebar() {
    const collapsed = this.layoutTarget.classList.toggle("is-collapsed")
    this.#markSidebarToggle(collapsed)
    this.#rememberSidebar(collapsed)
  }

  #restoreSidebar() {
    if (!this.hasLayoutTarget) return

    let stored = null
    try {
      stored = window.localStorage.getItem(this.constructor.SIDEBAR_KEY)
    } catch (e) {
      // zie #restoreView: geblokkeerde opslag mag de agenda niet breken
    }
    if (stored !== "collapsed") return

    this.layoutTarget.classList.add("is-collapsed")
    this.#markSidebarToggle(true)
  }

  #markSidebarToggle(collapsed) {
    if (!this.hasSidebarToggleTarget) return

    const knop = this.sidebarToggleTarget
    knop.setAttribute("aria-expanded", String(!collapsed))
    knop.classList.toggle("is-active", !collapsed)
    knop.setAttribute(
      "aria-label",
      collapsed ? knop.dataset.labelShow : knop.dataset.labelHide
    )
  }

  #rememberSidebar(collapsed) {
    try {
      window.localStorage.setItem(this.constructor.SIDEBAR_KEY, collapsed ? "collapsed" : "open")
    } catch (e) {
      // niet kunnen onthouden is geen reden om niet te kunnen klappen
    }
  }

  // --- in- en uitklappen op een smal scherm -------------------------------------
  //
  // Alleen zichtbaar onder de tabletbreedte; daarboven verbergt de CSS de knop en
  // staat de inhoud altijd open. De klasse zet alleen de staat, de breedte-vraag
  // blijft in de stylesheet.

  toggleFilters() {
    // Op de zijbalk zelf, niet op this.element: de controller hangt aan .agenda en
    // de stylesheet kijkt naar .filters.is-open.
    const open = this.panelTarget.classList.toggle("is-open")

    // De knop toont een chevron, geen woord. Voor wie het pijltje niet ziet blijft
    // aria-expanded het echte signaal; het label eromheen zegt wat de knop doet.
    this.toggleTarget.setAttribute("aria-expanded", String(open))
    this.toggleTarget.setAttribute(
      "aria-label",
      open ? this.toggleTarget.dataset.labelHide : this.toggleTarget.dataset.labelShow
    )
  }

  // Hoeveel filters staan er aan? Een dichtgeklapte balk mag geen zwarte doos zijn.
  #updateToggleCount(active) {
    // Twee knoppen, dezelfde boodschap: de chevron op smal en de knop op desktop.
    // Een weggeklapte balk mag niet verbergen dat er gefilterd wordt.
    const tellers = []
    if (this.hasToggleCountTarget) tellers.push(this.toggleCountTarget)
    if (this.hasSidebarCountTarget) tellers.push(this.sidebarCountTarget)

    tellers.forEach((teller) => {
      teller.textContent = active
      teller.classList.toggle("is-hidden", active === 0)
    })
  }

  // --- lijst of raster ----------------------------------------------------------

  selectView(event) {
    this.#applyView(event.currentTarget.dataset.view, { remember: true })
  }

  #restoreView() {
    let stored = null
    try {
      stored = window.localStorage.getItem(this.constructor.VIEW_KEY)
    } catch (e) {
      // localStorage kan geblokkeerd zijn (privacy-instellingen, iframe zonder
      // toestemming). Dan blijft het gewoon bij de standaardweergave.
    }
    if (stored) this.#applyView(stored, { remember: false })
  }

  #applyView(view, { remember }) {
    const list = view === "list"

    this.element.querySelectorAll(".event-grid")
      .forEach((grid) => grid.classList.toggle("event-grid--list", list))

    this.viewButtonTargets.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.view === view)
    })

    if (!remember) return
    try {
      window.localStorage.setItem(this.constructor.VIEW_KEY, view)
    } catch (e) {
      // zie hierboven; niet kunnen onthouden is geen reden om niet te wisselen
    }
  }

  // --- kalender ---------------------------------------------------------------

  previousMonth() {
    if (this.monthIndex > 0) {
      this.monthIndex -= 1
      this.#renderCalendar()
    }
  }

  nextMonth() {
    if (this.monthIndex < this.calendarGridTargets.length - 1) {
      this.monthIndex += 1
      this.#renderCalendar()
    }
  }

  toggleDay(event) {
    const button = event.currentTarget
    const date = button.dataset.date

    if (this.selectedDays.has(date)) {
      this.selectedDays.delete(date)
    } else {
      this.selectedDays.add(date)
    }

    button.classList.toggle("is-selected", this.selectedDays.has(date))
    this.apply()
  }

  #renderCalendar() {
    this.calendarGridTargets.forEach((grid, index) => {
      grid.classList.toggle("is-hidden", index !== this.monthIndex)
    })

    if (this.hasCalendarLabelTarget) {
      const current = this.calendarGridTargets[this.monthIndex]
      this.calendarLabelTarget.textContent = current ? current.dataset.label : ""
    }
  }

  // --- filteren ---------------------------------------------------------------

  reset() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.genreTargets.forEach((box) => { box.checked = false })
    this.selectedDays.clear()
    this.element.querySelectorAll(".calendar__day.is-selected")
      .forEach((day) => day.classList.remove("is-selected"))
    this.apply()
  }

  apply() {
    const term = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    const genres = this.genreTargets.filter((box) => box.checked).map((box) => box.value)
    const days = [...this.selectedDays]

    let visible = 0

    this.cardTargets.forEach((card) => {
      // Een evenement met speeldata over meerdere maanden staat met al zijn datums in
      // data-dates; het hoort dus bij elke gekozen dag waarop het speelt.
      const cardDates = (card.dataset.dates || "").split(" ").filter(Boolean)

      const matches =
        (days.length === 0 || cardDates.some((date) => days.includes(date))) &&
        (genres.length === 0 || genres.includes(card.dataset.genre)) &&
        (!term || (card.dataset.title || "").includes(term))

      card.classList.toggle("is-hidden", !matches)
      if (matches) visible += 1
    })

    // Een maandkop zonder zichtbare kaarten hoort mee te verdwijnen, anders blijft er
    // een lege sectietitel staan.
    this.sectionTargets.forEach((section) => {
      const anyVisible = section.querySelectorAll("[data-red2-agenda-target='card']:not(.is-hidden)").length > 0
      section.classList.toggle("is-hidden", !anyVisible)
    })

    const filtering = term !== "" || genres.length > 0 || days.length > 0

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("is-hidden", visible > 0)
    }

    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("is-hidden", !filtering)
    }

    if (this.hasCountTarget) {
      this.countTarget.textContent = this.#resultLabel(visible)
    }

    // Zoekterm telt als één, elk genre en elke gekozen dag als één.
    this.#updateToggleCount((term ? 1 : 0) + genres.length + days.length)
  }

  // De vertaalde vorm staat als data-attribuut op het element, door de server
  // gerenderd; hier wordt alleen het aantal ingevuld. Zo hangt deze controller niet
  // van de i18n-js-bundel af.
  #resultLabel(count) {
    const template = count === 1
      ? this.countTarget.dataset.labelOne
      : this.countTarget.dataset.labelOther

    return (template || "%{count}").replace("%{count}", count)
  }
}
